import { useQuery } from '@tanstack/react-query';
import { assetActions } from 'actions/asset';
import type { AssetInspectionRecord } from 'actions/asset';

export interface InspectionObservation {
  readonly element: string;
  readonly id: number;
  readonly defect: string;
  readonly conditionCode: string;
  readonly conditionScore: number;
  readonly workCost: number;
  readonly comments: string;
  readonly priority: string;
  readonly action: string;
  readonly suggestedTreatment: string | null;
  readonly span: number;
}

export interface MergedInspectionReport {
  readonly id: number;
  readonly inspectionDate: string;
  readonly inspectionType: string;
  readonly inspector: string;
  readonly comments: string;
  readonly signedOff: boolean;
  readonly averageScore: number;
  readonly totalCosts: number;
  readonly observations: InspectionObservation[];
}

const EMPTY_REPORTS: readonly MergedInspectionReport[] = [];

/**
 * Fetches inspection reports once per asset/record-set/refresh token.
 * Keep this hook mounted above tab panels so remounts do not restart GETs.
 */
const useFetchInspectionReports = (
  assetId: number,
  assetHubRefreshToken: number,
  inspectionRecords: readonly AssetInspectionRecord[],
  recordsLoading: boolean,
) => {
  const refreshData = assetHubRefreshToken > 0;
  const inspectionRecordIds = inspectionRecords
    .map((record) => String(record.id))
    .join(',');

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      'inspectionReports',
      assetId,
      inspectionRecordIds,
      assetHubRefreshToken,
    ],
    queryFn: async (): Promise<MergedInspectionReport[]> => {
      if (inspectionRecords.length === 0) {
        return [];
      }

      const uniqueRecords = Array.from(
        new Map(
          inspectionRecords.map((record) => [String(record.id), record]),
        ).values(),
      );

      const mergedDataRaw = await Promise.all(
        uniqueRecords.map(async (record) => {
          try {
            const reportResponse = await assetActions().getInspectionReport(
              Number(assetId),
              Number(record.id),
              refreshData,
            );
            const reportData = Array.isArray(reportResponse)
              ? reportResponse[0]
              : reportResponse;

            if (!reportData) return null;

            return {
              id: Number(record.id),
              inspectionDate: record.inspectionDate,
              inspectionType: record.inspectionType,
              inspector: record.inspector,
              comments: record.comments,
              signedOff:
                record.signedOff === 'true' || record.signedOff === true,
              averageScore: reportData?.averageScore ?? 0,
              totalCosts: reportData?.totalCosts ?? 0,
              observations: Array.isArray(reportData?.observations)
                ? (reportData.observations as InspectionObservation[])
                : [],
            };
          } catch {
            return null;
          }
        }),
      );

      return mergedDataRaw
        .filter((item): item is MergedInspectionReport => item !== null)
        .reverse();
    },
    enabled: assetId > 0 && !recordsLoading && inspectionRecordIds.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    inspectionReports: data ?? EMPTY_REPORTS,
    reportsLoading: isLoading,
    reportsError: isError,
  };
};

export default useFetchInspectionReports;
